import { Card } from '@components/card';
import { useDeleteModal } from '@components/generic-modals';
import { IconEdit } from '@components/icons/edit';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOutput } from '@components/icons/output';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';

import { useDeleteSmtpDomain, useRefreshSmtpDomainToken } from 'api/company/hooks';
import { EmailDomain, SMTP_AUTH_TYPE_OAUTH2 } from 'api/company/requests';

type SmtpDomainCardProps = {
  domain: EmailDomain;
  onEditDomain: () => void;
};

export const SmtpDomainCard = ({ domain, onEditDomain }: SmtpDomainCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteSmtpDomain();
  const refreshTokenMutation = useRefreshSmtpDomainToken();

  const isVerified = domain.isVerified;
  const isOAuth2 = domain.smtpAuthType === SMTP_AUTH_TYPE_OAUTH2;

  const handleDelete = () => {
    showDeleteModal({
      title: t('deleteDomainTitle'),
      description: t('deleteDomainDescription'),
      hasUserConfirmation: true,
      buttonText: t('remove'),
      buttonIcon: <IconTrash />,
      onConfirm: () => deleteMutation.mutate(domain.shortId),
    });
  };

  const handleRefreshToken = () => {
    refreshTokenMutation.mutate(domain.shortId);
  };

  return (
    <Card spaceX={4} spaceY={3}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" space={2} align="center">
          <IconEmailAddress />
          <div>
            <Stack orientation="horizontal" space={1} align="center">
              <Text text={domain.smtpHost ?? domain.domain} font="semibold12" />
              <Text
                text={isOAuth2 ? t('oauth2Label') : t('smtpLabel')}
                font="regular12"
                color="textSecondary"
              />
            </Stack>
            <Stack orientation="horizontal" space={2} align="center">
              <TextStatusIndicator
                variant={isVerified ? 'success' : 'pending'}
                text={isVerified ? t('verified') : t('unverified')}
                size="small"
              />
            </Stack>
          </div>
        </Stack>
        <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
          <Menu.Item label={t('edit')} icon={<IconEdit />} onSelect={onEditDomain} />
          {isOAuth2 && (
            <Menu.Item
              label={t('refreshToken')}
              icon={<IconOutput />}
              onSelect={handleRefreshToken}
            />
          )}
          <Menu.Item
            label={t('remove')}
            icon={<IconTrash />}
            onSelect={handleDelete}
            variant="danger"
          />
        </Menu>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    verified: 'Verified',
    unverified: 'Unverified',
    smtpLabel: 'SMTP',
    oauth2Label: 'SMTP (OAuth2)',
    edit: 'Edit',
    remove: 'Remove',
    refreshToken: 'Refresh token',
    deleteDomainTitle: 'Remove SMTP configuration',
    deleteDomainDescription:
      'Are you sure you want to remove this SMTP configuration? The associated email address will also be removed.',
  },
};
